import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Loader2, AlertCircle, ShieldCheck, Target, Trophy, ExternalLink, Sparkles, Share2,
} from "lucide-react";
import { ClinqLogo } from "../components/ClinqLogo";
import WebReportSections from "../components/founder/WebReportSections";
import { API, PUBLIC_APP_URL } from "../lib/api";

const READINESS_TARGET = 70; // Market Ready

/** Public, no-auth read-only view of a founder's shared Capital Readiness Report.
 *
 *  URL: /r/:token  →  fetches /api/public/readiness-report/{token}.
 *  Token is rotateable and expires 30 days from issue.
 */
export default function PublicReadinessReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr("");
      try {
        const { data: payload } = await axios.get(`${API}/public/readiness-report/${token}`);
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail || "";
          if (e?.response?.status === 410) {
            setErr(detail || "This share link has expired.");
          } else if (e?.response?.status === 404) {
            setErr("This share link is invalid or has been revoked.");
          } else {
            setErr(detail || "Couldn't load the report.");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#E5EEF1]" style={{ fontFamily: "Manrope, sans-serif" }}>
      <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" data-testid="public-report-logo">
            <ClinqLogo size="md" tone="teal" />
          </Link>
          <a
            href="/signup?role=founder&utm_source=shared_report"
            data-testid="public-report-cta-top"
            className="hidden rounded-lg bg-[#004B62] px-4 py-2 text-xs font-bold text-white hover:bg-[#00303F] sm:inline-flex"
          >
            Get your own CCR Score
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
        {loading && (
          <div data-testid="public-report-loading"
               className="flex items-center justify-center rounded-2xl bg-white p-12 text-sm text-neutral-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin"/> Loading shared report…
          </div>
        )}

        {!loading && err && (
          <div data-testid="public-report-error"
               className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center">
            <AlertCircle className="mx-auto h-8 w-8 text-amber-600"/>
            <h1 className="mt-3 text-2xl font-bold text-neutral-900">Report unavailable</h1>
            <p className="mt-2 text-sm text-amber-900">{err}</p>
            <Link
              to="/"
              data-testid="public-report-home-link"
              className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-[#00828F] hover:underline"
            >
              Go to Clinq home <ExternalLink className="h-3.5 w-3.5"/>
            </Link>
          </div>
        )}

        {!loading && !err && data && <ReportBody data={data} token={token} />}
      </main>

      <footer className="mx-auto max-w-5xl px-4 pb-12 pt-6 sm:px-6">
        <p className="text-center text-[11px] text-neutral-500">
          This is a public, read-only Clinq Capital Readiness Report. The CRS measures
          funder-readiness, not creditworthiness or guaranteed funding outcomes.
        </p>
      </footer>
    </div>
  );
}

function ReportBody({ data, token }) {
  const comp = data.company || {};
  const score = data.readiness_score || 0;
  const verified = data.verified_readiness || { status: "none" };
  const isVerified = verified.status === "verified" && verified.verified_score != null;
  const displayedScore = isVerified ? verified.verified_score : score;
  const metaLine = useMemo(() =>
    [comp.sector, comp.country, (comp.stage || "").toString()].filter(Boolean).join(" · "),
    [comp.sector, comp.country, comp.stage]
  );

  const [nativeAvailable, setNativeAvailable] = useState(false);
  useEffect(() => {
    if (typeof navigator === "undefined") return;
    setNativeAvailable(typeof navigator.share === "function");
  }, []);

  const shareWithImage = async () => {
    // Always re-share using the canonical /r/{token} URL on the production
    // domain — never `window.location.href` (which would be the preview
    // origin if someone is testing in preview).
    const url = token ? `${PUBLIC_APP_URL}/r/${token}` : PUBLIC_APP_URL;
    const name = comp.company_name || "This business";
    const text =
      `${name} scored ${displayedScore}/100 on Clinq's Capital Readiness Score — ` +
      `a funder-grade assessment for African founders. See where you stand:\n\n${url}`;
    const baseData = { title: "Clinq — Capital Readiness Score", text, url };
    try {
      const r = await fetch(`${API}/public/share-card/${token}.png`, { cache: "no-store" });
      if (r.ok) {
        const blob = await r.blob();
        const file = new File([blob], "clinq-readiness-score.png", { type: "image/png" });
        const dataWithFile = { ...baseData, files: [file] };
        if (navigator.canShare?.(dataWithFile)) {
          await navigator.share(dataWithFile);
          return;
        }
      }
    } catch (_e) { /* fall through */ }
    try {
      if (navigator.canShare?.(baseData)) await navigator.share(baseData);
      else await navigator.share?.(baseData);
    } catch (_e) { /* user dismissed */ }
  };

  return (
    <article>
      {/* Header card */}
      <section className="rounded-2xl bg-gradient-to-br from-[#00303F] via-[#0A4543] to-[#00828F] p-6 text-white sm:p-8"
               data-testid="public-report-header">
        <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4FB0BB]">
          <ShieldCheck className="h-3 w-3"/>
          {isVerified ? "Verified Capital Readiness Report" : "Indicative Capital Readiness Report"}
        </p>
        <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl"
            data-testid="public-report-company">
          {comp.company_name || "Verified business"}
        </h1>
        <p className="mt-1 text-sm text-white/70">{metaLine}</p>

        <div className="mt-6 flex flex-wrap items-end gap-x-6 gap-y-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">
              {isVerified ? "Verified CCR Score" : "Indicative CCR Score"}
            </p>
            <p className="text-6xl font-extrabold leading-none text-white"
               data-testid="public-report-score">{displayedScore}<span className="text-2xl text-white/40"> / 100</span></p>
          </div>
          <BandChip score={displayedScore}/>
          {isVerified && (
            <span data-testid="public-report-verified-badge"
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/25 px-3 py-1 text-xs font-bold text-emerald-200">
              <ShieldCheck className="h-3.5 w-3.5"/> Verified by Clinq
            </span>
          )}
          {!isVerified && displayedScore >= READINESS_TARGET && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-bold text-emerald-300">
              <Trophy className="h-3.5 w-3.5"/> Market Ready threshold reached
            </span>
          )}
        </div>

        {data.summary && (
          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-white/85">
            {data.summary}
          </p>
        )}

        {!isVerified && (
          <p className="mt-4 inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white/85"
             data-testid="public-report-indicative-note">
            <Sparkles className="h-3.5 w-3.5 text-[#ECC764]"/>
            This is an indicative AI-generated score. The business may upgrade to a Verified
            score (human-expert reviewed) for higher credibility with funders.
          </p>
        )}
      </section>

      {/* PDF-parity report sections — same content + plain language */}
      <div className="mt-6">
        <WebReportSections data={data}/>
      </div>

      {/* Call to action */}
      <section className="mt-8 rounded-2xl border border-[#00828F]/20 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#00828F]">
              <Target className="h-3 w-3"/> Want your own CCR Score?
            </p>
            <p className="mt-2 text-base font-bold text-neutral-900">
              Run a funder-grade readiness assessment on your business in minutes.
            </p>
            <p className="mt-1 text-sm text-neutral-600">
              Free quiz score · full Capital Readiness Report · personalised 90-day roadmap.
            </p>
          </div>
          <a
            href="/signup?role=founder&utm_source=shared_report"
            data-testid="public-report-cta-bottom"
            className="inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-5 py-3 text-sm font-bold text-white hover:bg-[#00303F]"
          >
            <Sparkles className="h-4 w-4"/> Get my CCR Score
          </a>
        </div>
        {nativeAvailable && token && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-100 pt-4">
            <p className="text-xs text-neutral-600">
              Share <span className="font-semibold text-neutral-900">{comp.company_name || "this score"}</span>{"'s"}
              {" "}readiness card — the image shows the real score.
            </p>
            <button
              onClick={shareWithImage}
              data-testid="public-report-share-image"
              className="inline-flex items-center gap-2 rounded-lg border border-[#00828F]/30 bg-[#00828F]/5 px-4 py-2 text-xs font-bold text-[#00647F] hover:bg-[#00828F]/10"
            >
              <Share2 className="h-3.5 w-3.5"/> Share with image
            </button>
          </div>
        )}
      </section>
    </article>
  );
}

function BandChip({ score }) {
  let label = "Pre-Readiness";
  let bg = "bg-rose-500/20";
  let text = "text-rose-200";
  if (score >= 85) { label = "Capital-Ready"; bg = "bg-emerald-500/25"; text = "text-emerald-200"; }
  else if (score >= 70) { label = "Investment-Grade"; bg = "bg-[#4FB0BB]/25"; text = "text-[#4FB0BB]"; }
  else if (score >= 55) { label = "Promising"; bg = "bg-amber-500/25"; text = "text-amber-200"; }
  else if (score >= 40) { label = "Developing"; bg = "bg-orange-500/25"; text = "text-orange-200"; }
  return (
    <span data-testid="public-report-band" className={`rounded-full px-3 py-1 text-xs font-bold ${bg} ${text}`}>
      {label}
    </span>
  );
}
